import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, ShieldCheck, Loader2 } from "lucide-react";

export default function GuardianApprove() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const initialAction = params.get("action");
  const [state, setState] = useState({ stage: token ? "ready" : "invalid", result: null, error: "" });

  const act = async (action) => {
    setState((s) => ({ ...s, stage: "loading", error: "" }));
    try {
      const { data } = await api.post("/auth/guardian-approve", { token, action });
      setState({ stage: "done", result: data, error: "" });
    } catch (err) {
      setState({ stage: "ready", result: null, error: apiError(err.response?.data?.detail) || "Something went wrong." });
    }
  };

  useEffect(() => {
    if (token && (initialAction === "approve" || initialAction === "deny")) act(initialAction);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-spotter-cream">
      <div className="w-full max-w-md bg-white border-4 border-black rounded-2xl p-7 brand-shadow text-center">
        <ShieldCheck className="w-10 h-10 mx-auto text-spotter-maroon mb-2" />
        <h1 className="font-display text-2xl font-bold text-spotter-maroon mb-3">Guardian Approval</h1>
        {state.stage === "invalid" && <p className="text-spotter-maroon" data-testid="guardian-approve-invalid">This approval link is invalid or has expired.</p>}
        {state.stage === "loading" && <Loader2 className="w-8 h-8 animate-spin text-spotter-maroon mx-auto" />}
        {state.stage === "ready" && (
          <>
            <p className="text-spotter-maroon mb-4 text-sm">A teen listed you as their parent/guardian on Spotter. Approve their account so they can log in, or deny it.</p>
            {state.error && <p className="text-red-700 text-sm mb-3">{state.error}</p>}
            <div className="flex gap-2 justify-center">
              <Button data-testid="guardian-approve-btn" onClick={() => act("approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Approve</Button>
              <Button data-testid="guardian-deny-btn" onClick={() => act("deny")} variant="outline" className="border-2 border-black gap-1"><X className="w-4 h-4" />Deny</Button>
            </div>
          </>
        )}
        {state.stage === "done" && (
          <>
            <p className="text-spotter-maroon mb-4" data-testid="guardian-approve-result">
              {state.result.status === "approved"
                ? <><b>{state.result.minor_name}</b> has been approved and can now log in to Spotter.</>
                : <><b>{state.result.minor_name}</b>'s account was denied and will remain blocked.</>}
              {state.result.already && <span className="block text-xs mt-1">(This request was already handled.)</span>}
            </p>
            <Button data-testid="guardian-approve-home" onClick={() => navigate("/")} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Go to Spotter</Button>
          </>
        )}
      </div>
    </div>
  );
}
